import React from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components/macro'
import DataTable from 'react-data-table-component'
import orderBy from 'lodash/orderBy'
import PropTypes from 'prop-types'
import SearchResultTitle from 'components/ui/SearchResultTitle'
import { t } from 'ttag'
import Dotdotdot from 'react-dotdotdot'
import CustomPagination from 'components/CustomPagination'
import SelectedFilters from 'components/SelectedFilters'
import SearchResultDescription from 'components/ui/SearchResultDescription'
import SearchResultSelectedFilters from 'components/ui/SearchResultSelectedFilters'
import ExtendedSearchResultInfo from 'components/ui/ExtendedSearchResultInfo'
import InfoCol from 'components/ui/InfoCol'
import ResultInfo from 'components/ui/ResultInfo'
import SearchFooter from 'components/ui/SearchFooter'
import ResultActions from 'components/ui/ResultActions'
import ResultAction from 'components/ui/ResultAction'
import ResultActionIcon from 'components/ui/ResultActionIcon'
import ResultUrlContainer from 'components/ui/ResultUrlContainer'
import LoadingSpinner from 'components/ui/LoadingSpinner'
import { getResultUrl } from 'util/hash'
import { PARTNERS_RESULTS_PER_PAGE } from 'store/constants/constants'
import SpinnerContainer from 'components/ui/SpinnerContainer'
import { show } from 'redux-modal'

const Link = styled.a`
  color: #003399;
  display: block;
  text-decoration: none;
  white-space: normal;
`
const Loading = styled.div`
  position: relative !important;
  margin-top: 50px;
`
const ErrorText = styled.p`
  color: #f31100;
`
const StyledDataTable = styled(DataTable)`
  font-family: 'Open Sans';
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px 4px 0 0;
  .rdt_TableHeader {
    display: none;
  }
  .rdt_TableRow {
    &:nth-child(odd) {
      background-color: #eaeaee;
    }
  }
  .rdt_TableHead {
    background-color: #003399;
    .rdt_TableCol {
      color: white;
      font-size: 12px;
      line-height: 17px;
      border-left: 1px solid white;
    }
    .rdt_TableCol_Sortable {
      color: white;
      font-size: 12px;
      line-height: 17px;
      padding: 10px 0;
    }
  }
  .rdt_TableCell {
    border-right: 1px solid #eaeaee;
    div:first-child {
      white-space: normal;
      text-overflow: initial;
    }
  }
  .rdt_TableFooter {
    justify-content: center;
  }
  .rdt_ExpanderRow {
    background-color: rgba(243, 243, 243, 0.35);
  }
`
const ExpandedRow = styled.ul`
  width: 100%;
  font-size: 13px;
  list-style-type: none;
  padding-left: 10px;
`

const ExpandedCell = styled.li`
  padding: 5px 0;
  font-size: 13px;
  & > div {
    display: inline-block;
  }
`
const CellLabel = styled.span`
  padding-right: 5px;
  display: inline;
  color: #636363;
`
const Info = styled.div`
  margin-bottom: 10px;
`
const InfoLink = styled.a`
  margin-left: 10px;
  cursor: pointer;
`

const handlePartnerName = partner => {
  let name
  let languageIndex = 0
  if ('en' in partner.translations && partner.translations.en.name_translated) {
    name = partner.translations.en.name_translated
  } else {
    while (!name) {
      name = partner.translations[Object.keys(partner.translations)[languageIndex]].name_translated
      languageIndex += 1
    }
  }

  return name
}

const getName = row => {
  if (row.translations) {
    if (row.translations.en?.name_translated) {
      return row.translations.en.name_translated
    }

    if (Array.isArray(row.translations)) {
      return row.translations[0].name_translated
    }
  }

  return row.name
}

const columns = [
  {
    name: 'Previous experience as',
    selector: 'experience',
    cell: row => (
      <div>
        {row.experience
          .map(e => `${e.substr(0, 1).toUpperCase()}${e.substr(1, e.length - 1)}`)
          .join(', ')}
      </div>
    )
  },
  {
    name: 'Organisation name',
    selector: 'name',
    cell: (row, clamp = 3) => (
      <Dotdotdot clamp={clamp}>
        <Link
          target="_blank"
          href={row.type === 'partner' ? `/partners/${row.id}` : `/partner-profiles/${row.id}`}
        >
          {row.name ? row.name : handlePartnerName(row)}
        </Link>
      </Dotdotdot>
    )
  },
  {
    name: 'Organisation name (EN)',
    selector: 'name',
    cell: (row, clamp = 3) => (
      <Dotdotdot clamp={clamp}>
        <Link
          target="_blank"
          href={row.type === 'partner' ? `/partners/${row.id}` : `/partner-profiles/${row.id}`}
        >
          {getName(row)}
        </Link>
      </Dotdotdot>
    )
  },
  {
    name: 'Projects in which it took part',
    selector: 'projects',
    cell: (row, clamp = 3) => (
      <Dotdotdot clamp={clamp}>{row.projects.map(project => project.acronym).join(', ')}</Dotdotdot>
    )
  },
  {
    name: 'Programmes financing the projects in which it took part',
    selector: 'programmes',
    cell: (row, clamp = 3) => (
      <Dotdotdot clamp={clamp}>
        {row.programmes.map(programme => programme.title).join(', ')}
      </Dotdotdot>
    )
  },
  {
    name: 'Programming periods',
    selector: 'programmes',
    cell: (row, clamp = 3) => (
      <Dotdotdot clamp={clamp}>
        {row.programmes.map(programme => programme.period.title).join(', ')}
      </Dotdotdot>
    )
  },
  {
    name: 'Country',
    selector: 'country',
    cell: row => <div>{(row.country || {}).title}</div>
  }
]

const customSort = (rows, field, direction) => {
  const handleField = row => {
    let fieldValue = row[field]
    if (field === 'country') {
      fieldValue = row[field].title
    } else if (field === 'name') {
      fieldValue = row[field] ? row[field] : handlePartnerName(row)
    }
    if (typeof fieldValue === 'string') {
      return fieldValue.toLowerCase()
    }
    return fieldValue
  }

  return orderBy(rows, handleField, direction)
}

const ExpandedComponent = ({ data }) => (
  <ExpandedRow>
    {columns.map(col => {
      const colValue = data[col.selector]
      return (
        <>
          <ExpandedCell>
            <CellLabel>{col.selector}: </CellLabel>
            {'cell' in col ? col.cell(data, 10) : colValue}
          </ExpandedCell>
        </>
      )
    })}
  </ExpandedRow>
)

function PartnerSearchResults({
  searchResults: { results = [], count, current, next, previous, total_pages: totalPages },
  isPending,
  error,
  isSidebarVisible,
  reduced,
  onPageChange,
  onExportToExcel,
  currentFilters,
  onGetResultUrl,
  resultHash,
  isExcelExportPending
}) {
  const handlePageChange = page => {
    onPageChange(page)
  }

  const handlePerRowsChange = page => {
    onPageChange(page)
  }

  const dispatch = useDispatch()
  let resultsComponent

  if (results.length === 0) {
    resultsComponent = <div className="no-results">{t`No results found`}</div>
  } else {
    resultsComponent = (
      <>
        <StyledDataTable
          columns={columns}
          data={results}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          expandableRows
          // defaultSortField="name"
          sortFunction={customSort}
          expandableRowsComponent={<ExpandedComponent data={results} />}
          responsive
          striped
          paginationDefaultPage={current}
          // paginationComponent={CustomPagination}
          // pagination
          // paginationServer
          // paginationTotalRows={count}
          // paginationPerPage={6}
          // paginationComponentOptions={{ noRowsPerPage: true }}
        />
        {count > PARTNERS_RESULTS_PER_PAGE && (
          <SearchFooter>
            <CustomPagination
              onPageChange={data => onPageChange(data)}
              count={count}
              current={current}
              next={next}
              previous={previous}
              totalPages={totalPages}
              section={'partners'}
            />
          </SearchFooter>
        )}
      </>
    )
  }
  if (error) {
    resultsComponent = <ErrorText>{t`An unexpected error occurred.`}</ErrorText>
  }
  return (
    <>
      {isSidebarVisible || !reduced ? (
        <>
          <SearchResultTitle>
            <h1>{t`Search results - partners`}</h1>
          </SearchResultTitle>
          <ExtendedSearchResultInfo>
            <InfoCol>
              <Info>
                {t`
                A partner may show several times in the lists (`}
                <a href="/faq/partner-what-is-it/" target="__blank">
                  {t`check the FAQ entry on partners`}
                </a>
                )
              </Info>
              <Info>
                {t`
                  Also, in a minority of cases it is possible that keep.eu placed a partner in a wrong region (`}
                <a href="/faq/geocoding-what-is-it-how-does-keep-eu-use-it/" target="__blank">
                  {t`read more about the challenges of geolocation`}
                </a>
                ).
              </Info>
              <Info>
                {t`Keep.eu users can contact potential partners after logging in (`}
                <a
                  href="/faq/partner-contacts-how-does-this-work-in-keep-eu/"
                  target="__blank"
                >{t`read more about how this works`}</a>
                )
              </Info>
              {!isPending && !error && (
                <ResultInfo>{t`${count
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} partners found`}</ResultInfo>
              )}
            </InfoCol>
          </ExtendedSearchResultInfo>
        </>
      ) : (
        <>
          <SearchResultTitle>
            <h1>{t`Search results - partners`}</h1>
          </SearchResultTitle>
          <ExtendedSearchResultInfo>
            <InfoCol>
              {!isPending && !error && (
                <ResultInfo>{t`${count
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} partners found`}</ResultInfo>
              )}
              <SearchResultSelectedFilters>
                <SelectedFilters currentFilters={currentFilters} selection={true} />
              </SearchResultSelectedFilters>
            </InfoCol>
            <InfoCol>
              <SearchResultDescription>{t`Look for partners and register to contact potential partners or to build your own profile. Results of searches are in alphabetical order of partners' names, and a partner may show more than once as it is not possible to fully consolidate all partnerships.`}</SearchResultDescription>
            </InfoCol>
          </ExtendedSearchResultInfo>
        </>
      )}
      {isPending ? (
        <Loading>
          <LoadingSpinner showText />
        </Loading>
      ) : (
        <>
          <ResultActions>
            <ResultAction onClick={onExportToExcel} id="prtnr_to_excel">
              {isExcelExportPending ? 'Exporting to Excel' : 'Export to Excel '}
              {isExcelExportPending ? (
                <SpinnerContainer>
                  <LoadingSpinner showText={false} />
                </SpinnerContainer>
              ) : (
                <ResultActionIcon className="far fa-file-excel" />
              )}
            </ResultAction>
            <>
              {resultHash === '' ? (
                <ResultAction onClick={onGetResultUrl} id="prtnr_get_url">
                  {t`Copy page URL`} <ResultActionIcon className="far fa-external-link" />
                </ResultAction>
              ) : (
                <>
                  <ResultUrlContainer
                    onClick={e => e.target.select()}
                    defaultValue={getResultUrl(resultHash)}
                  />
                  <InfoLink
                    target="_blank"
                    onClick={async () => {
                      const data = await fetch(
                        `/wp-json/wp/v2/pages?slug=url-get-results-url-how-to-shorten-the-urls`
                      )
                      const jsonData = await data.json()
                      const msg = jsonData[0].content.rendered
                      dispatch(show('infoModal', { message: msg }))
                    }}
                  >{t`How to shorten the URLs?`}</InfoLink>
                </>
              )}
            </>
            <br />
            <br />
          </ResultActions>
          <br />
          <br />
          <br />
          {resultsComponent}
        </>
      )}
    </>
  )
}

ExpandedComponent.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object])
}

PartnerSearchResults.propTypes = {
  searchResults: PropTypes.shape({
    results: PropTypes.array,
    count: PropTypes.number,
    current: PropTypes.number,
    next: PropTypes.string,
    previous: PropTypes.string,
    total_pages: PropTypes.number
  }),
  isPending: PropTypes.bool,
  error: PropTypes.bool,
  isSidebarVisible: PropTypes.bool,
  reduced: PropTypes.bool,
  onPageChange: PropTypes.func,
  onExportToExcel: PropTypes.func,
  currentFilters: PropTypes.array,
  onGetResultUrl: PropTypes.func,
  resultHash: PropTypes.string,
  isExcelExportPending: PropTypes.bool
}

export default PartnerSearchResults
